import { Query } from '@varo/graphql-schema-types';
import { useEffect, useRef, useState } from 'react';
import GetProfileInfo from 'services/graphql/queries/GetProfileInfo';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { saveUserIDForReactNative } from 'shared/utils/biometricAuth/biometricAuth';
import delay from 'shared/utils/delay/delay';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const POLLING_DELAY_MS = 2000;

/**
 * Hook for polling email updates.
 * Returns a function to wait for email update and a boolean indicating if polling is in progress.
 */
const useWaitForEmailUpdate = () => {
  const [executeProfileInfoQuery] = useCancellableQuery<Query>(GetProfileInfo);
  const [isPolling, setIsPolling] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  /**
   * Poll for email update
   * @param initialEmail The initial email to compare against
   * @param maxAttempts Maximum number of polling attempts
   * @returns Promise resolving to boolean indicating if email was updated
   */
  const waitForEmailUpdate = async (
    initialEmail: string,
    maxAttempts = 10,
  ): Promise<boolean> => {
    setIsPolling(true);
    // Cancel any ongoing request
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    const stopPollingIfCurrent = () => {
      if (abortControllerRef.current === abortController) {
        setIsPolling(false);
      }
    };

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (signal.aborted) {
        stopPollingIfCurrent();
        return false;
      }

      try {
        const data = await executeProfileInfoQuery(
          {},
          { requestPolicy: 'network-only' },
        );

        const updatedEmail = data.user?.customerInfo?.email?.address;

        // If email has changed from the initial email, update was successful
        if (updatedEmail && updatedEmail !== initialEmail) {
          setIsPolling(false);
          await saveUserIDForReactNative(updatedEmail);
          return true;
        }
      } catch (err) {
        if (err instanceof AbortError || signal.aborted) {
          stopPollingIfCurrent();
          return false;
        }

        logErrorWithContext(err, {
          featureArea: FeatureArea.FRAUD,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.LOW,
        });

        // Continue polling despite transient errors
      }

      await delay(POLLING_DELAY_MS, signal);
    }

    stopPollingIfCurrent();
    return false;
  };

  return {
    waitForEmailUpdate,
    isPolling,
  };
};

export default useWaitForEmailUpdate;
